/**
 * Structure detection: turn a raw 2-D string grid (as produced by the parsers)
 * into (a) the real header-row index when a title/blank preamble precedes it,
 * (b) per-row junk classification (blank / summary rows -> attention tray, never
 * silently dropped), and (c) per-column type inference stored on ColumnProfile
 * for downstream M4 typing and M5 NL routing.
 */

import type { AttentionReason, ColumnProfile, InferredType } from "./types";
import {
  isBooleanLike,
  isCoordinateLike,
  isDateLike,
  isNumeric,
  isStateCode,
  isZip,
  matchRatio,
} from "./recognizers";

/** Fraction of a column's non-empty values that must match a pattern to type it. */
export const TYPE_THRESHOLD = 0.8;

/**
 * Classify a column from its sample values. Order matters: more specific
 * patterns (zip, state, coordinate) are tested before the numeric/text
 * catch-alls, since a ZIP is also "numeric" and a state code is also "text".
 */
export function inferColumnType(values: string[]): InferredType {
  const nonEmpty = values.filter((v) => v.trim() !== "");
  if (nonEmpty.length === 0) return "text";
  const r = (pred: (v: string) => boolean) => matchRatio(nonEmpty, pred);

  if (r(isZip) >= TYPE_THRESHOLD) return "zip";
  if (r(isStateCode) >= TYPE_THRESHOLD) return "state";
  if (r(isCoordinateLike) >= TYPE_THRESHOLD) return "coordinate";
  if (r(isDateLike) >= TYPE_THRESHOLD) return "date";
  if (r(isBooleanLike) >= TYPE_THRESHOLD) return "boolean";
  if (r(isNumeric) >= TYPE_THRESHOLD) return "number";
  return "text";
}

const nonEmptyCount = (row: string[]): number => row.filter((c) => c.trim() !== "").length;

/**
 * Find the real header-row index. Skips a leading preamble of fully-blank rows
 * and sparse title banners (a single title cell in an otherwise-wide sheet),
 * returning the first full-width, word-like row. Defaults to 0 when nothing
 * clearly better is found (assume row 0 is the header — the common case).
 */
export function detectHeaderRow(rows: string[][]): number {
  if (rows.length === 0) return 0;
  const maxWidth = Math.max(...rows.map(nonEmptyCount), 0);
  if (maxWidth <= 1) return 0;

  const scan = Math.min(rows.length, 15);
  for (let i = 0; i < scan; i++) {
    const row = rows[i]!;
    const filled = nonEmptyCount(row);
    if (filled < 2) continue; // blank line or single-cell title banner
    if (filled < maxWidth * 0.5) continue; // sparse preamble row
    const cells = row.filter((c) => c.trim() !== "");
    // Headers are words, not values: most cells should be non-numeric/non-date.
    const wordish = cells.filter((c) => !isNumeric(c) && !isDateLike(c)).length / cells.length;
    if (wordish >= 0.7) return i;
  }
  return 0;
}

/**
 * Classify a data row as junk (dropped as `skipped`, not surfaced as a user
 * error — see RowStatus): fully-blank rows -> `empty_row`; trailing summary rows
 * (leading Total/Sum/Subtotal with the rest numeric/empty) -> `parse_error`.
 * Returns null for a normal data row.
 */
const SUMMARY_RE = /^(grand\s+total|total|totals|subtotal|sum|average|avg)$/i;

export function classifyJunkRow(cells: string[]): AttentionReason | null {
  if (nonEmptyCount(cells) === 0) return "empty_row";

  const firstIdx = cells.findIndex((c) => c.trim() !== "");
  const first = cells[firstIdx]!.trim();
  if (SUMMARY_RE.test(first)) {
    // A summary row: the label cell plus only numeric/blank cells after it.
    const rest = cells.slice(firstIdx + 1).filter((c) => c.trim() !== "");
    if (rest.every((c) => isNumeric(c))) return "parse_error";
  }
  return null;
}

/** A few distinct non-empty sample values for the Match panel + M5 routing. */
function sampleValues(values: string[], max = 5): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const t = v.trim();
    if (t === "" || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
    if (out.length >= max) break;
  }
  return out;
}

/**
 * Build a ColumnProfile per header column from the data rows (rows below the
 * detected header). Column count is driven by the header; short rows are
 * padded so every column gets a consistent value list.
 */
export function buildColumnProfiles(header: string[], dataRows: string[][]): ColumnProfile[] {
  return header.map((name, index) => {
    const values = dataRows.map((row) => row[index] ?? "");
    const nonEmpty = values.filter((v) => v.trim() !== "");
    return {
      index,
      header: name,
      inferred_type: inferColumnType(values),
      sample_values: sampleValues(values),
      non_empty_count: nonEmpty.length,
      total_count: values.length,
    };
  });
}
